import { useLingui } from "@lingui/react/macro";
import { workspaceNotificationInbox } from "@rakazo/contracts";
import { BotAvatar, Button, Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import { Bell, Target, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createSpaceRpc, selectedSpaceId, selectSpace } from "../lib/rpc";
import { useWorkspace } from "../lib/use-workspace";
import { errorText } from "../lib/user-error";

/** Reading this panel never marks a conversation read or acquires a computer lease. */
function useNotificationInbox() {
  const client = useMemo(() => createSpaceRpc(selectedSpaceId()), []);
  const state = useWorkspace(
    useCallback(async () => {
      const inbox = await client.notifications.inboxState();
      const [navigation, attention] = await Promise.all([
        client.spaces.list(),
        client.notifications.attention(),
      ]);
      return {
        asOf: inbox.asOf,
        entries: workspaceNotificationInbox(navigation, attention, inbox.cleared),
      };
    }, [client]),
    30000,
  );
  return state;
}

export function WorkspaceNotifications() {
  const { t } = useLingui();
  const state = useNotificationInbox();
  const [open, setOpen] = useState(false);
  const unread = (state.data?.entries ?? []).filter((item) => item.unread).length;
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) state.refresh();
      }}
    >
      <PopoverTrigger
        className="workspace-notification-trigger"
        aria-label={t`Notifications`}
        title={t`Notifications`}
      >
        <Bell size={19} />
        {unread > 0 && (
          <span
            role="img"
            className="workspace-notification-badge"
            aria-label={t`${unread} notifications`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="w-96 max-w-[calc(100vw-2rem)] overflow-hidden p-0"
      >
        <WorkspaceNotificationInbox onOpen={() => setOpen(false)} onClear={state.refresh} />
      </PopoverContent>
    </Popover>
  );
}

/** Also serves saved notification links; the dock opens the anchored panel. */
export function WorkspaceNotificationInbox({
  onOpen,
  onClear,
}: {
  onOpen?: () => void;
  onClear?: () => void;
} = {}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const state = useNotificationInbox();
  const [recent, setRecent] = useState(false);
  const [error, setError] = useState("");
  const [clearing, setClearing] = useState(false);
  const client = useMemo(() => createSpaceRpc(selectedSpaceId()), []);
  const entries = state.data?.entries ?? [];
  async function clear() {
    if (!state.data || clearing) return;
    setClearing(true);
    setError("");
    try {
      await client.notifications.clear({ before: state.data.asOf });
      state.refresh();
      onClear?.();
    } catch (error) {
      setError(errorText(error));
    } finally {
      setClearing(false);
    }
  }
  const shown = recent ? entries : entries.filter((item) => item.unread);
  return (
    <main className="flex max-h-[min(32rem,70dvh)] min-h-0 flex-col gap-3 bg-background p-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-sm font-medium">{t`Notifications`}</h1>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={clearing || !entries.some((item) => item.unread)}
            onClick={() => void clear()}
          >{t`Clear all`}</Button>
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={recent}
            onClick={() => setRecent(!recent)}
          >
            {recent ? t`Unread` : t`Recent`}
          </Button>
        </div>
      </div>
      {(state.error || error) && (
        <p role="alert" className="text-xs text-destructive">
          {error || state.error}
          <Button variant="ghost" size="sm" onClick={state.refresh}>{t`Retry`}</Button>
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!state.data && !state.error ? (
          <p
            role="status"
            className="py-6 text-center text-xs text-muted-foreground"
          >{t`Loading…`}</p>
        ) : !shown.length && !state.error ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            {recent ? t`No recent conversations` : t`No unread notifications`}
          </p>
        ) : (
          shown.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className="flex w-full min-w-0 items-start gap-3 rounded-lg px-2 py-3 text-left hover:bg-muted/40"
              onClick={() => {
                if (!selectSpace(entry.target.spaceId)) {
                  setError(t`Could not switch space`);
                  return;
                }
                onOpen?.();
                navigate(
                  "missionId" in entry.target
                    ? `/app/missions?id=${encodeURIComponent(entry.target.missionId)}`
                    : "groupId" in entry.target
                      ? `/app/g/${encodeURIComponent(entry.target.groupId)}`
                      : `/app/${encodeURIComponent(entry.target.botId)}`,
                );
              }}
            >
              {"missionId" in entry.target ? (
                <Target size={22} className="mt-1 shrink-0 text-muted-foreground" />
              ) : entry.color ? (
                <BotAvatar color={entry.color} identity={entry.key} size={28} />
              ) : (
                <Users size={22} className="mt-1 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-xs font-medium">
                  <span className="min-w-0 line-clamp-2">{entry.name}</span>
                  {entry.unread && <span className="size-1 shrink-0 rounded-full bg-foreground" />}
                </span>
                <span
                  data-testid="notification-preview"
                  className="mt-1 line-clamp-2 text-xs text-muted-foreground"
                >
                  {entry.attention ? (
                    <span className="mr-1 font-medium">
                      {entry.attention === "decision"
                        ? t`Decision needed`
                        : entry.attention === "failed"
                          ? t`Needs attention`
                          : t`Needs you`}
                      {entry.preview ? " · " : ""}
                    </span>
                  ) : null}
                  {entry.preview || (entry.attention ? "" : t`New reply`)}
                </span>
                <span className="mt-1 block truncate text-[10px] text-muted-foreground">
                  {entry.space}
                </span>
              </span>
            </button>
          ))
        )}
      </div>
    </main>
  );
}
